import {
  trainingProgramTableType,
  workoutPlanType,
} from "../../../../../providers/dataPlatformReportTypes";

/**
 * `selectedSystem` value of the Energy & Training Zones system. It has no
 * row in `score.systems[]` (backend ids 1–26), so it gets a sentinel that
 * can never collide with one — 0 already means "nothing selected".
 */
export const ENERGY_TRAINING_SYSTEM_ID = -1;

/** Ticket SD-6793 wording; the consultation calls it "Energy & Training". */
export const ENERGY_TRAINING_SYSTEM_NAME = "Energy & Training Zones";

/** The backend's value for a range the test could not measure. */
const EMPTY_RANGE = "0-0";

const isEmptyRange = (value: string | undefined): boolean =>
  !value || value.replace(/\s/g, "") === EMPTY_RANGE;

/** Zone rows of the report's training table, without its header row. */
const zoneRows = (workoutPlan: workoutPlanType | null | undefined) =>
  (workoutPlan?.trainingProgramTable ?? []).slice(1);

/**
 * True when the report's Training Program table carries a measurement: a
 * client without an AMR test gets the five zone rows with every range "0-0".
 */
export const hasTrainingTableData = (
  workoutPlan: workoutPlanType | null | undefined
): boolean =>
  zoneRows(workoutPlan).some(
    (row) =>
      !isEmptyRange(row.hr) ||
      !isEmptyRange(row.watts) ||
      !isEmptyRange(row.speed)
  );

/**
 * Rows for `PlatformTable`, row 0 being the backend's header row: the
 * Training Program table (zone / HR / watts / speed) joined by index with the
 * fueling table (fat / carb % and kcal/min). Moved unchanged from the legacy
 * Workout tab, except that the Watts and Speed columns are dropped when no
 * zone has that range — the backend sends "0-0" for what the test did not
 * measure (no bike → watts, no treadmill → speed), and the consultation graph
 * hides that axis in the same case.
 */
export const buildTrainingTableRows = (
  workoutPlan: workoutPlanType
): Record<string, string>[] => {
  const rows = zoneRows(workoutPlan);
  const hideWatts = rows.every((row) => isEmptyRange(row.watts));
  const hideSpeed = rows.every((row) => isEmptyRange(row.speed));
  return workoutPlan.trainingProgramTable.map(
    (el: trainingProgramTableType, index: number) => {
      const fuelingRow = workoutPlan.fuelingTable[index];
      return {
        c1: el.zone,
        c2: el.hr,
        ...(hideWatts ? {} : { c3: el.watts }),
        ...(hideSpeed ? {} : { c4: el.speed }),
        c5: fuelingRow?.fatburn,
        c6: fuelingRow?.carbburn,
        c7: fuelingRow?.average,
        c8: fuelingRow?.lowerend,
        c9: fuelingRow?.upperend,
      };
    }
  );
};

/** Per-zone tooltip copy (benefits / feels like / when to use) from the backend. */
export const buildTrainingTableInsights = (workoutPlan: workoutPlanType) =>
  workoutPlan.trainingProgramTable.map((el: trainingProgramTableType) => ({
    c5: el.benefits,
    c6: el.feels_like,
    c7: el.when_to_use,
  }));
